package ai.wagemix.web.components

import ai.wagemix.web.theme.WMIconSizeVar
import ai.wagemix.web.theme.WMIconStrokeVar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.key
import com.varabyte.kobweb.compose.css.CSSLengthNumericValue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.ariaHidden
import com.varabyte.kobweb.compose.ui.modifiers.ariaLabel
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.modifiers.flexShrink
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.dom.Span

/**
 * The icon box: a square of [WMIconSizeVar] whose `<svg>` inherits `currentColor`, so an
 * icon takes the colour of the text beside it.
 */
val WMIconStyle = CssStyle.base {
    Modifier.inlineFlex().flexShrink(0)
}

/**
 * Draws a Lucide [glyph] at [size] (default [WMIconSizeVar], 20px) with the design's 1.75px
 * stroke. A `null` [accessibilityString] marks the icon decorative (`aria-hidden`); a label
 * gives it `role="img"`.
 *
 * Compose HTML creates elements with `createElement`, which does not produce SVG nodes, so the
 * icon's markup is set as the box's inner HTML on mount. The box is keyed on [glyph], so a new glyph (the theme
 * toggle's moon becoming a sun) mounts a new box with the new markup. The markup is a constant from [WMIconGlyph]
 * and never user input.
 */
@Composable
fun WMIcon(
    glyph: WMIconGlyph,
    accessibilityString: String?,
    modifier: Modifier = Modifier,
    size: CSSLengthNumericValue = WMIconSizeVar.value()
) {
    val a11y = if (accessibilityString == null) Modifier.ariaHidden() else Modifier.role("img").ariaLabel(accessibilityString)
    key(glyph) {
        Span(
            attrs = WMIconStyle.toModifier()
                .size(size)
                .then(a11y)
                .then(modifier)
                .toAttrs {
                    ref { element ->
                        element.innerHTML = glyph.svg()
                        onDispose { }
                    }
                }
        )
    }
}

private fun WMIconGlyph.svg(): String {
    return "<svg xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" width=\"100%\" height=\"100%\" " +
        "fill=\"none\" stroke=\"currentColor\" stroke-width=\"var(${WMIconStrokeVar.name}, 1.75)\" " +
        "stroke-linecap=\"round\" stroke-linejoin=\"round\" focusable=\"false\">$markup</svg>"
}

/** `display: inline-flex`, which Compose HTML's `DisplayStyle` only offers under a legacy name. */
fun Modifier.inlineFlex(): Modifier = styleModifier { property("display", "inline-flex") }
